<!-- 
 CRIEI ESTE COMPONENTE PARA CUSTOMIZAR A QUESTÃO DAS PAGINAÇÕES NAS TABELAS, E TENTAR REPRODUZIR COMO ERA
 ANTES DO SYNCFORK PARA O CW 4.0.4... O COMPONENTE Pagination.vue PRECISA DE TODOS OS REGISTROS DA TABELA
 PARA CRIAR AS PAGINAÇÕES, O QUE NÃO É VIÁVEL EM ALGUNS CASOS (COMO EM CONVERSATIONS VIEW MODAL) 

 NESTE COMPONENTE, PASSAMOS O TOTAL DE REGISTROS E A PAGINA ATUAL (DEVERÃO SER CONTROLADOS NOS COMPONENTES
 QUE UTILIZARÃO O CustomPagination.vue), E A CADA MUDANÇA DE PÁGINA, FAZEMOS UMA REQUISIÇÃO PARA O BACKEND
 RETORNAR OS REGISTROS.
-->
<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import FilterSelect from 'dashboard/components-next/filter/inputs/FilterSelect.vue';

const props = defineProps({
  total: {
    type: Number,
    default: 0,
  },
  currentPage: {
    type: Number,
    default: 1,
  },
  defaultPageSize: {
    type: Number,
    default: 10,
  },
  showPageSizeSelector: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['pageChange', 'pageSizeChange']);
const { t } = useI18n();

// const currentPage = ref(1);
const pageSize = ref(props.defaultPageSize);

const totalPages = computed(() => {
  return Math.max(Math.ceil(props.total / pageSize.value), 1);
});

const start = computed(() => {
  return props.total === 0 ? 0 : (props.currentPage - 1) * pageSize.value + 1;
});

const end = computed(() => {
  return Math.min(props.currentPage * pageSize.value, props.total);
});

const pageSizeOptions = [10, 20, 50, 100].map(size => ({
  label: `${t('REPORT.PAGINATION.PER_PAGE_TEMPLATE', { size })}`,
  value: size,
}));

const getFormattedPages = (s, e) => {
  const formatter = new Intl.NumberFormat(navigator.language);
  return Array.from({ length: e - s + 1 }, (_, i) => formatter.format(s + i));
};

const visiblePages = computed(() => {
  if (totalPages.value <= 3) return getFormattedPages(1, totalPages.value);
  if (props.currentPage === 1) return getFormattedPages(1, 3);
  if (props.currentPage === totalPages.value) {
    return getFormattedPages(totalPages.value - 2, totalPages.value);
  }
  return getFormattedPages(props.currentPage - 1, props.currentPage + 1);
});

// Navegação
const goToPage = page => {
  const target = Math.min(Math.max(1, page), totalPages.value);
  if (props.currentPage !== target) {
    props.currentPage = target;
    emit('pageChange', target);
  }
};

const nextPage = () => {
  if (props.currentPage < totalPages.value) {
    goToPage(props.currentPage + 1);
  }
};

const prevPage = () => {
  if (props.currentPage > 1) {
    goToPage(props.currentPage - 1);
  }
};

const firstPage = () => goToPage(1);
const lastPage = () => goToPage(totalPages.value);

// Watch para resetar página se total ou pageSize mudar
watch([() => props.total, pageSize], () => {
  if (props.currentPage > totalPages.value) {
    goToPage(totalPages.value);
  }
});

// Watch para emitir mudança de pageSize
watch(pageSize, newValue => {
  emit('pageSizeChange', newValue);
  goToPage(1); // volta para a página 1 se mudar o tamanho da página
});
</script>

<template>
  <div class="flex items-center justify-between">
    <div class="flex flex-1 items-center gap-2 justify-between">
      <p class="text-sm truncate text-n-slate-11 mb-0">
        {{ $t('REPORT.PAGINATION.RESULTS', { start, end, total }) }}
      </p>
      <div class="flex items-center gap-2">
        <FilterSelect
          v-if="showPageSizeSelector"
          v-model="pageSize"
          variant="outline"
          hide-icon
          class="[&>button]:text-n-slate-11 [&>button]:hover:text-n-slate-12 [&>button]:h-6"
          :options="pageSizeOptions"
        />
        <nav class="isolate inline-flex items-center gap-1.5">
          <Button
            icon="i-lucide-chevrons-left"
            ghost
            slate
            sm
            class="!size-6"
            :disabled="currentPage === 1"
            @click="firstPage"
          />
          <Button
            icon="i-lucide-chevron-left"
            ghost
            slate
            sm
            class="!size-6"
            :disabled="currentPage === 1"
            @click="prevPage"
          />
          <Button
            v-for="page in visiblePages"
            :key="page"
            xs
            outline
            :color="Number(page) === currentPage ? 'blue' : 'slate'"
            class="!h-6 min-w-6"
            @click="goToPage(Number(page))"
          >
            <span
              class="text-center"
              :class="{ 'text-n-brand': Number(page) === currentPage }"
            >
              {{ page }}
            </span>
          </Button>
          <Button
            icon="i-lucide-chevron-right"
            ghost
            slate
            sm
            class="!size-6"
            :disabled="currentPage === totalPages"
            @click="nextPage"
          />
          <Button
            icon="i-lucide-chevrons-right"
            ghost
            slate
            sm
            class="!size-6"
            :disabled="currentPage === totalPages"
            @click="lastPage"
          />
        </nav>
      </div>
    </div>
  </div>
</template>
